import { RRule } from 'rrule'
import dayjs from 'dayjs'
import { convertToRRuleOptions } from '../../utils/rruleCompat'
import * as dateUtil from '../../utils/DateUtils'

interface SplitRecurrenceArgs {
  /** 원본 이벤트의 RRule (origOptions와 between 메서드를 사용) */
  orgRRule: RRule
  /** 원본 이벤트의 시작일 (tz 정보가 박힌 Date 가능) */
  orgStart: Date
  /** 분할 시점 — 새 이벤트의 시작일 (tz 정보가 박힌 Date 가능) */
  newStart: Date
  /** 새 이벤트의 RRule origOptions (mutate 하지 않음) */
  newOrigOptions: Record<string, unknown>
}

export interface SplitRecurrenceResult {
  /** 원본 이벤트에 다시 할당할, 분할 시점 직전까지로 종료된 RRule */
  orgRRule: RRule
  /**
   * count 기반 반복일 때만 새 이벤트용 RRule을 반환한다.
   * until/endless 반복은 caller가 보유한 vevent.rrule을 그대로 유지한다.
   */
  newRRule?: RRule
}

/**
 * 반복 이벤트의 '이후 일정 수정(future)' 분기에서 원본 RRule을 분할 시점 직전까지로
 * 종료시키고, count 기반 반복이라면 새 이벤트의 count도 남은 횟수로 조정한다.
 *
 * node-ical(rrule-temporal)에서 파싱된 RRule의 origOptions는 camelCase 키
 * (byDay/byHour 등) 와 exDate/rDate/strict 같은 추가 키를 포함하므로 그대로
 * new RRule(...)에 넘기면 throw 한다. convertToRRuleOptions로 정규화한 뒤 사용한다.
 */
export function splitRecurrenceForFutureEdit(args: SplitRecurrenceArgs): SplitRecurrenceResult {
  const { orgRRule, orgStart, newStart, newOrigOptions } = args

  const rruleOptions = convertToRRuleOptions(
    orgRRule.origOptions as unknown as Record<string, unknown>,
  )

  if ((rruleOptions.count ?? 0) > 0) {
    const periodStart = dateUtil.getDateZero(orgStart)
    const periodEnd = dateUtil.getDateZero(newStart)
    const consumedCount = orgRRule.between(periodStart, periodEnd).length
    rruleOptions.count = consumedCount
    const splitOrgRRule = new RRule(rruleOptions)

    // newOrigOptions를 mutate 하지 않도록 스프레드 (caller의 원본 보존)
    const splitNewOptions = { ...newOrigOptions } as { count?: number } & Record<string, unknown>
    if (typeof splitNewOptions.count === 'number') {
      splitNewOptions.count = splitNewOptions.count - consumedCount
    }
    return { orgRRule: splitOrgRRule, newRRule: new RRule(splitNewOptions) }
  }

  delete rruleOptions.count
  const newStartTz = (newStart as Date & { tz?: string }).tz
  const newUntil = dayjs(newStart).tz(newStartTz).add(-1, 'day').toDate()
  ;(newUntil as Date & { tz?: string }).tz = (orgStart as Date & { tz?: string }).tz
  rruleOptions.until = newUntil
  return { orgRRule: new RRule(rruleOptions) }
}
